<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';

import {
    apiTriggerPurchaseBlockNotifications,
    type AuctionItemEntity,
    AuctionState,
    type BackendErrorCode,
    LeadIntent,
    TriggerPurchaseBlockNotificationsSaleType,
    useApiAuctionDetail,
    useApiUpdateAccountPersonalData,
    getApiAuctionDetailQueryKey,
    type UserMeta,
} from '~/apiClient';
import { isAuctionItemEntityWithCurrentPrince } from '~/types/auction.type';
import { NotificationDuration } from '~/types/notifications.type';
import { isAuctionItemEntityWithNegotiation } from '~/types/sale.type';

interface Props {
    auction: AuctionItemEntity;
}
// PROPS
const { auction } = defineProps<Props>();

// COMPOSABLES
const queryClient = useQueryClient();
const { tt, tn } = useTypedI18n();
const { notifyError, notifyGenericError } = useNotification();
const { data: session, isLoggedIn } = useAuthUtils();
const { trackAdpPlaceBid, updateTrackingCompanyMetrics } = useTracking();
const { openAuthModal } = useModals();
const { hasRestrictedNotice, hasInspectionNotice, isBailNeeded, activeAutoBidAmount, hasPurchaseBlock, purchaseBlockReasons } = useAuctionStatus(
    toRef(() => auction)
);
const bailModalState = useModalState<boolean>();
const bidModalState = useModalState<boolean>();
const { captureLeadContacts, closeLeadModal } = useSdpLead();
const { mutateAsync: mutatePersonalData } = useApiUpdateAccountPersonalData();
const { logError } = useLogs();
const { isOnboardingInProgress } = useOnboarding();
const { refetch } = useApiAuctionDetail(auction.id);

// DATA
const hasPlaceBid = computed(() => auction.state === AuctionState.Running);
const hasGoToNegotiation = computed(() => isLoggedIn.value && isAuctionItemEntityWithNegotiation(auction));
const showSection = computed(() => hasRestrictedNotice.value || hasInspectionNotice.value || hasPlaceBid.value || hasGoToNegotiation.value);
const userMeta = computed<UserMeta | undefined>(() => auction.userMeta);
const bidFlowInProgress = ref(false);
const isPurchaseBlockModalOpen = ref(false);
// Frozen at the click, so a meta update while the modal is open does not change its text. The modal sits at the template
// root, so it stays open when the section hides (auction over, restricted notice).
const blockedReasons = ref<BackendErrorCode[]>([]);
const bidButtonLabel = computed(() => (userMeta.value?.autoBid?.active ? tt(`sdp.Auction.bidButtonActiveAutoBid`) : tt(`sdp.Auction.bidButton`)));

// HANDLERS
const bidFlow = async (): Promise<void> => {
    bidFlowInProgress.value = true;
    trackAdpPlaceBid();

    await (async (): Promise<void> => {
        const customerLeadContacts = await captureLeadContacts({ sale: auction, intentType: LeadIntent.PlaceBid });

        if (!customerLeadContacts) return;

        if (!isLoggedIn.value || isOnboardingInProgress.value) {
            if (!(await openAuthModal({ email: customerLeadContacts.email, phone: customerLeadContacts.phone, action: 'ADP_BID' }))) return;

            await refetch();
        }

        if (!session.value?.user.phone) {
            mutatePersonalData({ data: { phone: customerLeadContacts.phone } }).catch(error =>
                logError(`Error while saving phone in ADP`, { auctionId: auction.id, userId: session.value?.user.id, apiError: error })
            );
        }

        if (hasPurchaseBlock.value) {
            // Report the blocked attempt, whatever the cause (fire-and-forget; the outcome is irrelevant to the flow).
            apiTriggerPurchaseBlockNotifications({
                saleType: TriggerPurchaseBlockNotificationsSaleType.Auction,
                saleId: auction.id,
            }).catch(() => undefined);
            blockedReasons.value = [...purchaseBlockReasons.value];
            isPurchaseBlockModalOpen.value = true;

            return;
        }

        if (hasRestrictedNotice.value) {
            notifyGenericError();

            return;
        }

        if (isBailNeeded.value) {
            if (!(await bailModalState.open())) return;

            await until(isBailNeeded).toBe(false);
        }

        if (!userMeta.value?.canBid) {
            return;
        }

        if (await bidModalState.open()) {
            await queryClient.refetchQueries({ queryKey: getApiAuctionDetailQueryKey(auction.id), type: 'active' }); // Refetch not strictly necessary, redundant with update via WS
            updateTrackingCompanyMetrics();
        }
    })();

    bidFlowInProgress.value = false;
};

const closeModals = (): void => {
    closeLeadModal();
    bailModalState.cancel();
    bidModalState.cancel();
    isPurchaseBlockModalOpen.value = false;
};

// WATCHERS
watch(isLoggedIn, () => {
    // Reset internal state on login change
    closeModals();
});

watch(
    () => auction.state,
    value => {
        if ([AuctionState.Expired, AuctionState.Canceled].includes(value) && bidFlowInProgress.value) {
            closeModals();
            notifyError({
                title: tt(`sdp.Auction.bid.expiredError.title`),
                description: tt(`sdp.Auction.bid.expiredError.description`),
                duration: NotificationDuration.NoTimeout,
            });
        }
    }
);
</script>

<template>
    <div v-if="showSection" class="flex flex-col justify-center bg-white p-4 lg:p-6">
        <div class="flex flex-col gap-3">
            <SdpSalePanelNoticeRestricted v-if="hasRestrictedNotice" :sale="auction" />
            <template v-else>
                <SdpSalePanelNoticeBase
                    v-if="hasInspectionNotice"
                    :message="tt(`sdp.notice.inspectionExpired`)"
                    icon-name="lucide:triangle-alert"
                    icon-class="text-warning-500"
                />

                <template v-if="hasPlaceBid">
                    <span v-if="activeAutoBidAmount">
                        {{ tt(`sdp.Auction.bid.activeAutoBid`) }}:
                        <span class="text-primary font-medium">
                            {{ tn(activeAutoBidAmount, 'currency') }}
                        </span>
                    </span>

                    <UButton
                        size="lg"
                        :ui="buttonResponsiveSizeUi('lg', 'xl')"
                        :label="bidButtonLabel"
                        block
                        :loading="bidFlowInProgress"
                        @click="bidFlow"
                    />

                    <LazyAdpBailModal
                        v-if="isBailNeeded"
                        v-model="bailModalState.isOpen"
                        :auction="auction"
                        @success="bailModalState.confirm(true)"
                    />

                    <LazyAdpBidModal
                        v-if="userMeta?.canBid && isAuctionItemEntityWithCurrentPrince(auction)"
                        v-model="bidModalState.isOpen"
                        :auction="auction"
                        @success="bidModalState.confirm(true)"
                    />
                </template>
            </template>

            <SdpSalePanelGoToNegotiation v-if="hasGoToNegotiation" :sale="auction" />
        </div>
    </div>
    <LazySdpPurchaseBlockModal v-if="blockedReasons.length" v-model="isPurchaseBlockModalOpen" :reasons="blockedReasons" />
</template>
